import React, { FC, useState, useEffect, useCallback, ReactNode, ChangeEvent, FormEvent, Dispatch, SetStateAction } from 'react'
import { useLocation, useNavigate, useParams, Route, Routes } from 'react-router-dom'
import { useIntl } from 'react-intl'
import xss from 'xss'
import momenttz from 'moment-timezone'

import Backdrop from './Backdrop'
import { useTypedSelector } from '../../hooks/useTypedSelector'
import { useActions } from '../../hooks/useActions'
import CustomModal from '../ui/CustomModal'
import InteractiveModal from '../ui/InteractiveModal'
import Agenda from './agenda/Agenda'
import FAQ from './faq/FAQ'
import Home from './home/Home'
import HeaderNav from './HeaderNav'
import LottiePlayer from './LottiePlayer'
import '../../styles/landingPage.scss'

type LandingPageProps = {
    data: any
    eventSlug: string
    setEventSlug: Dispatch<SetStateAction<string>>
    eventList: any
}

const LandingPage: FC<LandingPageProps> = ({data, eventSlug, setEventSlug, eventList}) => {
    const [code, setCode] = useState('')
    const [eventOpen, setEventOpen] = useState(false)
    const [eventState, setEventState] = useState('countdown')
    const [showModal, setShowModal] = useState(false)
    const [modalContent, setModalContent] = useState<ReactNode | null>(null)
    const [showInteractiveModal, setShowInteractiveModal] = useState(false)
    const [agreeAnimation, setAgreeAnimation] = useState(false)
    const [startAnimation, setStartAnimation] = useState(false) 
    const [showBackdrop, setShowBackdrop] = useState(false)
    const [showActiveBackdrop, setShowActiveBackdrop] = useState(false)
    const [showMenu, setShowMenu] = useState(false)
    const [walkinName, setWalkinName] = useState('')
    const [walkinCompany, setWalkinCompany] = useState('')
    const [walkinEmail, setWalkinEmail] = useState('')
    const [googler, setGoogler] = useState(false)
    const [googlerName, setGooglerName] = useState('')
    const [showSailingBtn, setShowSailingBtn] = useState(true)
    const [firmLogo, setFirmLogo] = useState('')
    const [playStoreInput, setPlayStoreInput] = useState(false)
    const [playStoreId, setPlayStoreId] = useState('')
    const [playStoreIdValidate, setPlayStoreIdValidate] = useState<'id_invalid' | 'id_empty' | null>(null)

    const {
        onUserLogin,
        onGetEventDetail,
        removeUserError,
        onUserWalkinLogin,
        initUser,
        onUserGooglerLogin,
        onAgreeAnimation,
        onCheckUser,
    } = useActions()

    const {
        user: {
            isWalkin,
            isCreatingGoogler,
            error: userError,
            auth,
            user: { _id, logo },
        },
        event: { error: eventError, event, timeNow },
    } = useTypedSelector((state) => state)

    const intl = useIntl()

    const location = useLocation()
    const navigate = useNavigate()
    const params = useParams()

    /**
     * 獲取資料庫該服務 event id 資料
     */
    useEffect(() => {
        onGetEventDetail()
    }, [])

    /**
     * 初始化定時任務：設定倒數至活動開始開啟輸入框
     */
    useEffect(() => {
        const now = timeNow
        let timer = 0
        if (data) {
            if (now > 0) {
                if (Date.parse(data[0].attributes.openTime) >= now) {
                    timer = window.setTimeout(() => {
                        return setEventOpen(true) 
                        
                    }, Date.parse(data[0].attributes.openTime) - now)
                } else return setEventOpen(true)
            }
            console.log(`開放登入：${new Date(data[0].attributes.openTime)}`)
        }

        return () => {
            clearTimeout(timer)
        }
    }, [timeNow, data])

    /**
     * 設定倒數至 event open 時間把 countdown 改成 "課程準備中"
     */
    useEffect(() => {
        const now = timeNow
        let timer = 0
        if (data) {
            if (now > 0) {
                if (Date.parse(data[0].attributes.openTime) >= now) {
                    timer = window.setTimeout(() => {
                        return setEventState('prepare')
                    }, Date.parse(data[0].attributes.openTime) - now)
                } else return setEventState('prepare')
            }
        }

        return () => {
            clearTimeout(timer)
        }
    }, [timeNow, data])

    /**
     * 設定倒數至 event start 改成 “現正熱映”
     */
    useEffect(() => {
        const now = timeNow
        let timer = 0
        if (data) {
            if (now > 0) {
                if (Date.parse(data[0].attributes.startTime) >= now) {
                    timer = window.setTimeout(() => {
                        return setEventState('live')
                    }, Date.parse(data[0].attributes.startTime) - now)
                } else return setEventState('live')
            }
            // console.log(`活動開始：${new Date(data[0].attributes.startTime)}`)
        }

        return () => {
            clearTimeout(timer)
        }
    }, [timeNow, data])

    /**
     * 設定倒數至 end time + 15 分鐘 改成 “课程结束”
     */
    useEffect(() => {
        const now = timeNow
        let timer = 0
        
        if (data) { 
            if (now > 0) {
                if (Date.parse(data[0].attributes.endTime) + 900000 >= now) {
                    timer = window.setTimeout(() => {
                        return setEventState('recap')
                    }, Date.parse(data[0].attributes.endTime) + 900000 - now)
                } else return setEventState('recap')
            }
            // console.log(`活動結束：${new Date(data[0].attributes.endTime)}`)
        }

        return () => {
            clearTimeout(timer)
        }
    }, [timeNow, data])

    /**
     * 設定倒數至 end time + 3hr 網頁自動跳轉
     */
    // useEffect(() => {
    //     const now = timeNow
    //     if (data) { 
    //         if (now > 0) {
    //             if (Date.parse(data[0].attributes.endTime) + 10800000 <= now) {
    //                 window.location.href = 'https://adsacademy.googlecnapps.cn/course';
    //             }
    //         }
    //         console.log(`網頁跳轉時間: ${new Date(Date.parse(data[0].attributes.endTime) + 10800000)}`)
    //     } 
    // }, [timeNow, data])

    /**
     * 根據語言及活動 id 訂定初始畫面
     */
    useEffect(() => {
        if (params) {
            const param = params['*'] as string

            if (param) {
                const [, slug] = param.split('/')

                if (slug) {
                    setEventSlug(slug)
                    sessionStorage.setItem('eventSlug',slug);
                } else {
                    setEventSlug(param)
                    sessionStorage.setItem('eventSlug',param);
                }
            } else {
                navigate('/404', { replace: false })
            }
        }
    }, [params])

    /**
     * 頁面重新導向
     */
    // console.log(window.location.hash.slice(2, 16));
    // const endEvent = [
    //     '#/adsacademy0425',
    //     '#/adsacademy0518',
    //     '#/adsacademy0525',
    //     '#/adsacademy0713',
    //     '#/adsacademy0718',
    //     '#/adsacademy0720',
    //     '#/adsacademy0725',
    //     '#/adsacademy0815',
    //     '#/adsacademy0817',
    // ]
    // useEffect(() => {
    //     const url = window.location.hash.slice(0,16)
    //     if (endEvent.includes(url)) {
    //         window.location.href = 'https://adsacademy.googlecnapps.cn/course';
    //       }
    // }, [eventSlug])

    /**
     * 頁面不存在
     */
    // const eventList = [
    //     'adsacademy0425',
    //     'adsacademy0518',
    //     'adsacademy0525',
    //     'adsacademy071301',
    //     'adsacademy071801',
    //     'adsacademy0720',
    //     'adsacademy0725',
    //     'adsacademy0815',
    //     'adsacademy0817',
    //     'adsacademy0822',
    //     'adsacademy0824',
    //     'adsacademy0831',
    // ]
    useEffect(() => {
        if(eventList[0]){
            if (!eventList.includes(eventSlug)) navigate('/404', { replace: false });
        }
    }, [eventList, eventSlug])

    /**
     * 判斷是否存在錯誤訊息
     */
    useEffect(() => {
        if (eventError || userError) return handleMessageModal()
    }, [eventError, userError])

    /**
     * 判斷 query string
     */
    useEffect(() => {
        if (location.search) handleCheckSearch(location.search)
    }, [location.search])

    /**
     * 判斷 uid 是否成功登入，若 uid 為 walkin，導向至輸入用戶名稱及公司頁面
     */
    useEffect(() => {
        if (auth) navigate(`/main`, { replace: true })
        else {
            if (isWalkin) navigate(`/walkin/${eventSlug}`, { replace: true })
            else if (isCreatingGoogler) navigate(`/g/${eventSlug}`, { replace: true })
            else navigate(`${location.pathname}${location.search}`, { replace: true })
        }
    }, [auth, isWalkin, isCreatingGoogler])

    // useEffect(() => {
    //     handleStartAnimationFlow()
    //     setFirmLogo(logo)
    // }, [logo])



    /**
     * 若存在錯誤訊息，顯示提示框。若訊息為重複登入初始化用戶登入資訊。
     * 若是由後台刪除資料庫用戶名單，不顯示提示框只初始化用戶登入資訊。
     */
    const handleMessageModal = () => {
        let errorMessage = ''

        if (userError) {
            if (userError === 'USER_CODE_ERROR') {
                errorMessage = intl.formatMessage({ id: 'landingPage.USER_CODE_ERROR' })
            } else if (userError === 'USER_EVENT_ID_ERROR') {
                errorMessage = intl.formatMessage({ id: 'landingPage.USER_EVENT_ID_ERROR' })
            } else if (userError === 'USER_LOGIN_IN_DUPLICATE_ERROR') {
                errorMessage = intl.formatMessage({ id: 'landingPage.LoginDuplicated' })
                initUser()
            } else if (userError === 'USER_LIST_DROP') {
                initUser()
                removeUserError()
                return
            } else if (userError === 'USER_LOG_OUT') {
                initUser()
                return
            }
        } else errorMessage = intl.formatMessage({ id: 'landingPage.EVENT_ERROR' })

        setModalContent(<div id="login-msg-modal-content">{errorMessage}</div>)
        setShowModal(true)
        if (userError) removeUserError()
    }

    /**
     * 判斷 googler and uid query string
     * @param search query string
     */
    const handleCheckSearch = (search: string): void => {
        const queryStr = new URLSearchParams(search)

        for (const params of queryStr.entries()) {
            if (params[0] === 'uid') {
                setCode(params[1])
                onCheckUser({ code: params[1] })
            }
            if (params[0] === 'userid') {
                setCode(params[1])
                onCheckUser({ code: params[1] })
            }
            if (params[0] === 'googler') handleGoogler()
            if (params[0] === 'store') setPlayStoreInput(true)
        }
    }

    /**
     * 處理 query string googler 等於 true
     */
    const handleGoogler = () => {
        setGoogler(true)
        setEventOpen(true)
    }

    /**
     * 提交登入碼登入會場
     * @param e form event
     */
    const handleSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>, eventId: string, channelId: string) => {
            e.preventDefault()
            const loginCode = code.toLocaleLowerCase().trim()

            const ex = '\\W'

            if (loginCode.match(ex)) {
                setModalContent(<div id="login-msg-modal-content">参会码须为英数字</div>)
                setShowModal(true)

                return
            }

            if (!loginCode) {
                setModalContent(
                    <div id="login-msg-modal-content">{intl.formatMessage({ id: 'landingPage.USER_CODE_EMPTY' })}</div>
                )
                setShowModal(true)
                return
            }

            const searchQuery = location.search

            onUserLogin({ loginCode, searchQuery, eventPackage: event, eventSlug: eventId, googler, channelId })
            setCode('')
        },
        [code, location.search, event]
    )

    /**
     * 提交 walkin 資訊並登入會場
     * @param e form event
     */
    const handleWalkinSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            const name = walkinName.trim()
            const company = walkinCompany.trim()
            const email = walkinEmail.trim()

            if (!_id) {
                setModalContent(
                    <div id="login-msg-modal-content">{intl.formatMessage({ id: 'landingPage.USER_ID_ERROR' })}</div>
                )
                setShowModal(true)
                navigate(`/${eventSlug as string}`, { replace: true })
                return
            }

            onUserWalkinLogin({
                _id,
                walkinName: name,
                walkinCompany: company,
                walkinEmail: email,
                eventSlug,
                eventPackage: event,
            })

            setWalkinName('')
            setWalkinCompany('')
        },
        [_id, walkinName, walkinCompany, walkinEmail, eventSlug]
    )

    /**
     * 提交 googler 資訊並登入會場
     * @param e form event
     */
    const handleGooglerSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            const name = googlerName.trim()

            if (!_id) {
                setModalContent(
                    <div id="login-msg-modal-content">{intl.formatMessage({ id: 'landingPage.USER_ID_ERROR' })}</div>
                )
                setShowModal(true)
                navigate(`/${eventSlug as string}`, { replace: true })
                return
            }

            onUserGooglerLogin({ _id, googlerName: name })

            setGooglerName('')
            setWalkinCompany('')
        },
        [_id, googlerName, eventSlug]
    )

    /**
     * 登入框取值
     * @param e change event
     */
    const HandleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        setCode(value)
    }, [])

    /**
     * 關閉常見問題彈窗
     */
    const handleCloseModal = useCallback(() => {
        setShowModal(false)
    }, [])

    /**
     * toggle 行動裝置 backdrop
     */
    const handleBackdropShow = useCallback(() => {
        setShowBackdrop((state) => !state)
        setShowMenu((state) => !state)
    }, [])

    /**
     * 關閉行動裝置 backdrop
     */
    const handleBackdropRemove = useCallback(() => {
        setShowBackdrop(false)
        setShowMenu(false)
    }, [])

    const handleCloseInteractiveModal = useCallback(() => {
        setShowActiveBackdrop(false)
        setShowInteractiveModal(false)
    }, [])

    const handleStartAnimationFlow = () => {
        // if (anima && anima.everAgree) return
        // else {
        if (logo) {
            setShowInteractiveModal(true)
            setShowActiveBackdrop(true)
            setAgreeAnimation(false)
            setStartAnimation(false)
        }
        // }
    }

    const handleAgreeAnimation = (playStoreInput: boolean) => {
        if (!playStoreInput) {
            setFirmLogo(logo)
            setAgreeAnimation(true)
            setShowInteractiveModal(false)
            if (code) onAgreeAnimation({ code, agree: true, playStoreId: '' })
        } else {
            if (!playStoreId) setPlayStoreIdValidate('id_empty')
            else {
                const regex = new RegExp(
                    /^https:\/\/((play\.google\.com\/store\/apps\/details\?id=)|(apps\.apple\.com\/))/g
                )

                if (!regex.test(playStoreId)) {
                    setPlayStoreIdValidate('id_invalid')
                } else {
                    setPlayStoreIdValidate(null)
                    setFirmLogo(logo)
                    setAgreeAnimation(true)
                    setShowInteractiveModal(false)
                    if (code) onAgreeAnimation({ code, agree: true, playStoreId: xss(playStoreId) })
                }
            }
        }
    }

    const handleDisagreeAnimation = () => {
        setFirmLogo('img/avatar/googler.svg')
        setShowInteractiveModal(false)
        setAgreeAnimation(true)
        handleLongPressProgressBar()

        if (code) onAgreeAnimation({ code, agree: false, playStoreId: '' })
    }

    const handleLongPressProgressBar = useCallback(() => {
        setStartAnimation(true)

        window.setTimeout(() => {
            setShowInteractiveModal(false)
            setShowActiveBackdrop(false)
            setAgreeAnimation(false)
            setStartAnimation(false)
        }, 12000)
    }, [])

    const handleClickProgressBar = useCallback(() => {
        console.log('click is triggered')
    }, [])

    const handleDownloadVideo = () => {
        const aLink = document.createElement('a')
        aLink.target = '_blank'

        if (logo && logo !== 'img/avatar/googler.svg') {
            aLink.href = `https://oss.uppmkt.com/202205/SOA_web/dl/index.html?q=${code.toUpperCase()}`
            aLink.download = `${code}.mp4`
        } else {
            aLink.href = `https://oss.uppmkt.com/202205/SOA_web/dl/index.html?q=Google`
            aLink.download = `Google.mp4`
        }

        document.body.appendChild(aLink)
        aLink.click()
        document.body.removeChild(aLink)
    }

    return (
        <main className="landing-page-wrap">
            <img
                src={logo ? logo : 'img/avatar/googler.svg'}
                alt=""
                style={{ display: 'none' }}
            />
            <section className="landing-page-layout" id="landing-page-layout">
                <InteractiveModal
                    handleDisagreeAnimation={handleDisagreeAnimation}
                    handleAgreeAnimation={handleAgreeAnimation}
                    showModal={showInteractiveModal}
                    handleCloseInteractiveModal={handleCloseInteractiveModal}
                    playStoreInput={playStoreInput}
                    playStoreId={playStoreId}
                    setPlayStoreId={setPlayStoreId}
                    playStoreIdValidate={playStoreIdValidate}
                    handleDownloadVideo={handleDownloadVideo}
                    firmLogo={firmLogo}
                    code={code}
                >
                    {modalContent}
                </InteractiveModal>
                {agreeAnimation && (
                    <LottiePlayer
                        firmLogo={firmLogo}
                        startAnimation={startAnimation}
                        handleLongPressProgressBar={handleLongPressProgressBar}
                        handleClickProgressBar={handleClickProgressBar}
                    />
                )}

                <Backdrop showBackdrop={showActiveBackdrop} startAnimation={startAnimation} animation={true} />
                <section className="landing-page-layout-main">
                    <HeaderNav
                        handleBackdropShow={handleBackdropShow}
                        handleBackdropRemove={handleBackdropRemove}
                        eventSlug={eventSlug}
                        showMenu={showMenu}
                    />
                    <div className="landing-page-layout-content" id="landing-page-layout-content">
                        <CustomModal handleCloseModal={handleCloseModal} showModal={showModal}>
                            {modalContent}
                        </CustomModal>
                        <Backdrop showBackdrop={showBackdrop} startAnimation={startAnimation} animation={false} />

                        {/* {showSailingBtn && code && (
                            <SailingButton
                                setShowSailingBtn={setShowSailingBtn}
                                handleStartAnimationFlow={handleStartAnimationFlow}
                            />
                        )} */}

                        <Routes>
                            <Route
                                path="/*"
                                element={
                                    <Home
                                        handleSubmit={handleSubmit}
                                        eventOpen={eventOpen}
                                        code={code}
                                        HandleInputChange={HandleInputChange}
                                        eventSlug={eventSlug}
                                        auth={auth}
                                        handleWalkinSubmit={handleWalkinSubmit}
                                        setWalkinName={setWalkinName}
                                        setWalkinCompany={setWalkinCompany}
                                        walkinName={walkinName}
                                        walkinCompany={walkinCompany}
                                        googler={googler}
                                        googlerName={googlerName}
                                        setGooglerName={setGooglerName}
                                        handleGooglerSubmit={handleGooglerSubmit}
                                        walkinEmail={walkinEmail}
                                        setWalkinEmail={setWalkinEmail}
                                        data={data}
                                        eventState={eventState}
                                    />
                                }
                            />
                        </Routes>
                        {data &&  <Agenda eventSlug={eventSlug} data={data} />}
                        {data && <FAQ data={data} />} 
                    </div>
                    <div className="landing-page-layout-background-all">
                        <div className="landing-page-layout-background-home"></div>
                        <div className="landing-page-layout-background-agenda"></div>
                        <div className="landing-page-layout-background-faq"></div>
                    </div>
                </section>
            </section>
        </main>
    )
}

export default LandingPage
